import { Trans } from '@lingui/macro'
import { ZERO_ADDRESS } from 'constants/misc'
import { SPORT_SUBGRAPH_URL } from 'constants/subgraph'
import request, { gql } from 'graphql-request'
import useActiveWeb3React from 'hooks/useActiveWeb3React'
import React from 'react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components/macro'
import { ThemedText } from 'theme'
import { isChainAllowed } from 'utils/switchChain'
import useInterval from 'utils/useInterval'

import RowBets from './RowBets'

const Container = styled.div`
  width: 100%;
  margin-top: 1rem;
`

const PageButtons = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 1rem;
`

const Arrow = styled.div`
  color: ${({ theme }) => theme.text1};
  padding: 0 20px;
  :hover {
    cursor: pointer;
  }
`

interface IAcornSportBetGameProvider {
  id: string
  sportType: string
  leagueName: string
}

interface IAcornSportBetGame {
  gameIndex: string
  provider: IAcornSportBetGameProvider
  homeTeam: string
  awayTeam: string
  homeScore?: string
  awayScore?: string
  statusId?: string
  volumen?: string
  volumenHome?: string
  volumenAway?: string
  volumenDraw?: string
}

interface IAcornSportBet {
  id: string
  betIndex: string
  player: string
  amount: string
  game: IAcornSportBetGame
  betPosition: string
  date: string
  claimed?: boolean
}

function HistoryBets() {
  const [bets, setBets] = useState<IAcornSportBet[]>([])
  const [page, setPage] = useState(1)

  const { account, chainId, connector } = useActiveWeb3React()
  const chainAllowed = chainId && isChainAllowed(connector, chainId)
  const apiUrl = SPORT_SUBGRAPH_URL[chainAllowed ? chainId : 137]

  const getBets = useCallback(async () => {
    const response = await request(
      apiUrl,
      gql`
        query getBets($address: String!) {
          acornSportBets(where: { player: $address }, orderBy: date, orderDirection: desc, first: 100) {
            id
            betIndex
            player
            amount
            betPosition
            date
            claimed
            game {
              gameIndex
              provider {
                id
                sportType
              }
              homeTeam
              awayTeam
              homeScore
              awayScore
              statusId
              volumen
              volumenHome
              volumenAway
              volumenDraw
            }
          }
        }
      `,
      {
        address: account?.toLowerCase() ?? ZERO_ADDRESS,
      }
    )

    setBets(response.acornSportBets)
  }, [account, apiUrl])

  useEffect(() => {
    getBets()
  }, [getBets])

  const ITEMS = 5

  const sortedHistory = useMemo(() => {
    return bets.length > 0 ? bets.slice(ITEMS * (page - 1), page * ITEMS) : []
  }, [bets, page])

  const maxPage = sortedHistory.length > 0 ? Math.ceil(bets.length / ITEMS) : 0

  useInterval(getBets, 10000)

  return (
    <Container>
      {bets && bets.length > 0 ? (
        <Container>
          <ThemedText.LargeHeader mb="1rem">
            <Trans>History Bets</Trans>
          </ThemedText.LargeHeader>
          {sortedHistory.map((item, index) => (
            <React.Fragment key={index}>
              <RowBets item={item} getBets={getBets} />
            </React.Fragment>
          ))}
          <PageButtons>
            <Arrow
              onClick={() => {
                setPage(page === 1 ? page : page - 1)
              }}
            >
              <ThemedText.Body color={page === 1 ? 'text' : 'primary'}> {'<'} </ThemedText.Body>
            </Arrow>

            <ThemedText.Body>
              <Trans>Page</Trans> {page} <Trans>of</Trans> {maxPage}
            </ThemedText.Body>
            <Arrow
              onClick={() => {
                setPage(page === maxPage ? page : page + 1)
              }}
            >
              <ThemedText.Body color={page === maxPage ? 'textDisabled' : 'primary'}> {'>'} </ThemedText.Body>
            </Arrow>
          </PageButtons>
        </Container>
      ) : (
        ''
      )}
    </Container>
  )
}

export default HistoryBets
